import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Search, User, Loader2, FileText, Check } from "lucide-react";

interface Customer {
  id: string;
  first_name: string;
  last_name: string | null;
  email: string | null;
  customer_number: string | null;
}

interface Attachment {
  filename: string;
  contentType: string;
  size: number;
  partNumber: string;
  attachmentIndex?: number;
}

interface SaveAttachmentToCustomerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachment: Attachment | null;
  emailId: string;
  messageId: string | null;
  accountId: string;
  onSaved?: (customerId: string, customerName: string, filename: string) => void;
}

export const SaveAttachmentToCustomerDialog = ({
  open,
  onOpenChange,
  attachment,
  emailId,
  messageId,
  accountId,
  onSaved,
}: SaveAttachmentToCustomerDialogProps) => {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load customers on search
  useEffect(() => {
    const loadCustomers = async () => {
      if (!open) return;
      
      setLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        let query = supabase
          .from('customers')
          .select('id, first_name, last_name, email, customer_number')
          .eq('user_id', session.user.id)
          .order('first_name', { ascending: true })
          .limit(50);

        if (searchQuery.trim()) {
          query = query.or(`first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%,customer_number.ilike.%${searchQuery}%`);
        }

        const { data, error } = await query;

        if (error) {
          console.error('Error loading customers:', error);
        } else {
          setCustomers(data || []);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(loadCustomers, 300);
    return () => clearTimeout(debounce);
  }, [open, searchQuery]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setSelectedCustomer(null);
    }
  }, [open]);

  const handleSave = async () => {
    if (!selectedCustomer || !attachment) return;

    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Fehler",
          description: "Sie müssen angemeldet sein.",
          variant: "destructive",
        });
        return;
      }

      // Call edge function to fetch attachment and save to customer
      const { data, error } = await supabase.functions.invoke('fetch-email-attachment', {
        body: {
          emailId,
          messageId,
          accountId,
          attachmentIndex: attachment.attachmentIndex,
          partNumber: attachment.partNumber,
          filename: attachment.filename,
          customerId: selectedCustomer.id,
        }
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        const customerFullName = `${selectedCustomer.first_name} ${selectedCustomer.last_name || ''}`.trim();
        toast({
          title: "Erfolgreich gespeichert",
          description: `"${attachment.filename}" wurde in der Kundenakte von ${customerFullName} gespeichert.`,
        });
        
        // Notify parent about the save with filename
        onSaved?.(selectedCustomer.id, customerFullName, attachment.filename);
        
        onOpenChange(false);
      } else {
        throw new Error(data?.error || 'Unbekannter Fehler');
      }
    } catch (error: any) {
      console.error('Error saving attachment:', error);
      toast({
        title: "Fehler beim Speichern",
        description: error.message || "Der Anhang konnte nicht gespeichert werden.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Anhang in Kundenakte speichern
          </DialogTitle>
          <DialogDescription>
            Wählen Sie einen Kunden aus, um den Anhang "{attachment?.filename}" in dessen Akte zu speichern.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Kunde suchen (Name, E-Mail, Kundennummer)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Customer List */}
          <div className="border rounded-lg">
            <ScrollArea className="h-[250px]">
              {loading ? (
                <div className="flex items-center justify-center h-full py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : customers.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full py-8 text-muted-foreground">
                  <User className="h-8 w-8 mb-2" />
                  <p className="text-sm">
                    {searchQuery ? "Keine Kunden gefunden" : "Keine Kunden vorhanden"}
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {customers.map((customer) => (
                    <button
                      key={customer.id}
                      onClick={() => setSelectedCustomer(customer)}
                      className={`w-full flex items-center gap-3 p-3 text-left transition-colors hover:bg-muted/50 ${
                        selectedCustomer?.id === customer.id ? 'bg-primary/10 border-l-2 border-l-primary' : ''
                      }`}
                    >
                      <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        <User className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {customer.first_name} {customer.last_name || ''}
                        </p>
                        <p className="text-sm text-muted-foreground truncate">
                          {customer.email || customer.customer_number || 'Keine Kontaktdaten'}
                        </p>
                      </div>
                      {selectedCustomer?.id === customer.id && (
                        <Check className="h-5 w-5 text-primary flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Selected Customer Info */}
          {selectedCustomer && (
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
              <Check className="h-4 w-4 text-primary" />
              <span className="text-sm">
                Ausgewählt: <strong>{selectedCustomer.first_name} {selectedCustomer.last_name || ''}</strong>
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={!selectedCustomer || saving}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Speichern...
              </>
            ) : (
              'In Kundenakte speichern'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};